/**
 * One-click active-speaker framing: split the timeline into one scene per
 * transcript speaker turn, then frame every scene on that speaker's face.
 *
 * Composes the two AI Producer → Framing actions — `Create Scenes per Speaker`
 * and `Active Speaker Framing (Speaker-Face Matching) → Auto-Frame All Scenes`
 * — so Creator Studio and the Content Studio reframe modal run the same path.
 *
 * Both steps replace what was there: scene creation replaces the timeline's
 * scenes and framing overwrites each scene's crop.
 *
 * Speaker turns come from the transcript alone, so the scenes are split even
 * when no speaker→face mapping exists; those scenes are then framed face-only
 * until faces are mapped (PLAT-11992).
 */

import { LayoutEngine } from '@src/editor/models/layout/LayoutEngine';
import { err, ok, type Result } from '@src/services/common/Result';
import { deduceFacesForFraming } from '@src/services/framing/faceDeductions';
import { SmartFramingService } from '@src/services/framing/SmartFramingService';
import { createScenesPerSpeaker } from '@src/services/framing/zoomCall';
import { SceneService } from '@src/services/SceneService';
import { LogManager } from '@src/utility/Logger';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { NaturalDimensions } from '@src/editor/types/aspectRatio';
import type { SceneDetection } from '@src/editor/types/sceneDetection';
import type { FaceDetection } from '@src/services/framing/types';

const logger = LogManager.getLogger('service:autoFrameScenesPerSpeaker');

export interface AutoFrameScenesPerSpeakerInput {
  editor: IVideoEditor;
  faceDetections: FaceDetection[];
  /**
   * transcript `speaker_label` → `face_label`s, see
   * `buildSpeakerLabelToFaceLabelsMap`. Empty frames the scenes face-only.
   */
  speakerLabelToFaceLabels: Record<string, string[]>;
  /** Shot detections in media time; scenes are also split at each cut. */
  shots?: ReadonlyArray<SceneDetection> | null;
  /** Keyed by `VideoElement.source`; gives each new scene a cover crop. */
  naturalMediaDimensions?: Record<string, NaturalDimensions>;
  /**
   * Numeric target aspect ratio for the crops. Omit for 9:16 — that is
   * `SmartFramingService`'s own default.
   */
  targetAspectRatio?: number;
}

export interface AutoFrameScenesPerSpeakerOutput {
  /** Scenes on the timeline after the split. */
  sceneCount: number;
  /** How many of them got an active-speaker crop. */
  framedCount: number;
}

export function autoFrameScenesPerSpeaker({
  editor,
  faceDetections,
  speakerLabelToFaceLabels,
  shots,
  naturalMediaDimensions,
  targetAspectRatio,
}: AutoFrameScenesPerSpeakerInput): Result<AutoFrameScenesPerSpeakerOutput> {
  if (faceDetections.length === 0) {
    return err('PRECONDITION', 'No face detections available for this story');
  }

  if (!createScenesPerSpeaker({ editor, shots, naturalMediaDimensions })) {
    return err(
      'PRECONDITION',
      'Could not create scenes per speaker — no transcript speaker turns overlap this timeline',
    );
  }

  const sceneCount = editor.timeline.scenes.length;

  const framingService = new SmartFramingService(
    editor,
    new SceneService(),
    new LayoutEngine(),
  );
  // Faces nobody mapped that framing can work out itself (PLAT-12143).
  const speakerMap = deduceFacesForFraming(
    () => editor.getOriginalTranscript(),
    faceDetections,
    speakerLabelToFaceLabels,
  );
  const hasMapping = Object.keys(speakerMap).length > 0;
  const framedCount = hasMapping
    ? framingService.autoFrameAllScenesWithSpeakerContext(
        faceDetections,
        speakerMap,
        targetAspectRatio,
      )
    : framingService.autoFrameAllScenes(faceDetections, targetAspectRatio);

  logger.info('Created scenes per speaker and auto-framed them', {
    sceneCount,
    framedCount,
    framing: hasMapping ? 'by speaker' : 'face-only (no speaker→face mapping)',
    targetAspectRatio,
  });

  return ok({ sceneCount, framedCount });
}
